(() => {
  const initGallery = (section) => {
    section.querySelectorAll('[data-mp-pattern-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';

      const viewport = gallery.querySelector('.mp-pattern-gallery__viewport');
      const track = gallery.querySelector('.mp-pattern-gallery__track');
      const finalCard = track?.querySelector('[data-gallery-final-card]');
      const photos = Array.from(track?.querySelectorAll(':scope > [data-gallery-photo]') || []);
      const remainingPhotos = photos.map((photo, index) => {
        const image = photo.querySelector('img');
        const width = Number(image?.getAttribute('width')) || image?.naturalWidth || 1;
        const height = Number(image?.getAttribute('height')) || image?.naturalHeight || 1;
        return { photo, index, ratio: Math.max(0.35, Math.min(3, width / height)) };
      });
      const columns = [];
      const gallerySeed = Number(gallery.dataset.gallerySeed) || 0;
      let singleRun = 0;
      let pairedRun = 0;
      let pairedColumnCount = 0;

      while (remainingPhotos.length) {
        const first = remainingPhotos.shift();
        let photoHash = (first.index + 1) ^ gallerySeed;
        photoHash = Math.imul(photoHash ^ (photoHash >>> 16), 0x7feb352d);
        photoHash = Math.imul(photoHash ^ (photoHash >>> 15), 0x846ca68b);
        photoHash = (photoHash ^ (photoHash >>> 16)) >>> 0;
        const next = remainingPhotos[0];
        const compatible = next &&
          (next.ratio < 0.86) === (first.ratio < 0.86) &&
          Math.abs(next.ratio - first.ratio) < 0.28;
        const shouldPair = compatible && pairedRun < 2 &&
          (singleRun >= 2 || first.ratio >= 0.86 || photoHash % 5 < 3);
        const pair = shouldPair ? remainingPhotos.shift() : null;
        const column = document.createElement('div');
        column.className = `mp-pattern-gallery__column mp-pattern-gallery__column--${pair ? 'paired' : 'single'}`;
        column.setAttribute('role', 'presentation');
        column.appendChild(first.photo);
        if (pair) column.appendChild(pair.photo);
        const firstShare = pair ? (pairedColumnCount % 2 === 0 ? 0.56 : 0.44) : 1;
        if (pair) {
          first.photo.style.flexGrow = firstShare;
          pair.photo.style.flexGrow = 1 - firstShare;
          pairedColumnCount += 1;
          pairedRun += 1;
          singleRun = 0;
        } else {
          singleRun += 1;
          pairedRun = 0;
        }
        if (finalCard) track.insertBefore(column, finalCard);
        else track.appendChild(column);
        columns.push({ element: column, ratios: pair ? [first.ratio, pair.ratio] : [first.ratio], firstShare });
      }

      const slides = Array.from(track?.children || []);
      const controls = gallery.querySelector('.slider-buttons');
      const previousButton = controls?.querySelector('[name="previous"]');
      const nextButton = controls?.querySelector('[name="next"]');
      const scrollTrack = controls?.querySelector('.slider-scrollbar__track');
      const scrollIndicator = controls?.querySelector('.slider-scrollbar__indicator');
      const currentSlide = controls?.querySelector('.slider-counter--current');
      const totalSlides = controls?.querySelector('.slider-counter--total');
      const story = gallery.querySelector('.mp-pattern-gallery__story');
      const storyContent = gallery.querySelector('.mp-pattern-gallery__story-content');
      const finalCardContent = gallery.querySelector('.mp-pattern-gallery__final-content');
      if (!viewport || !controls || !scrollTrack || !scrollIndicator || !story) return;

      const maxScroll = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const slideStarts = () => [...new Set(slides.map((slide) => Math.round(slide.offsetLeft)))].sort((a, b) => a - b);
      const sizeColumns = () => {
        const height = track.clientHeight;
        columns.forEach(({ element, ratios, firstShare }) => {
          const availableHeight = ratios.length === 2 ? height - 12 : height;
          const width = ratios.length === 2
            ? Math.max(ratios[0] * availableHeight * firstShare, ratios[1] * availableHeight * (1 - firstShare))
            : ratios[0] * height;
          const fittedWidth = Math.max(ratios.length === 2 ? 110 : 150, Math.min(600, width));
          element.style.width = `${Math.round(fittedWidth)}px`;
          if (ratios.length === 1 && width > 600) {
            element.style.height = `${Math.round(fittedWidth / ratios[0])}px`;
            element.style.alignSelf = 'center';
          } else {
            element.style.height = '100%';
            element.style.alignSelf = 'stretch';
          }
        });
      };

      const updateControls = () => {
        const maximum = maxScroll();
        const position = Math.max(0, viewport.scrollLeft);
        controls.hidden = maximum <= 1;
        previousButton.disabled = position <= 1;
        nextButton.disabled = position >= maximum - 1;

        const starts = slideStarts();
        scrollTrack.style.setProperty('--num-slides', Math.max(1, starts.length));
        const indicatorTravel = Math.max(0, scrollTrack.clientWidth - scrollIndicator.clientWidth);
        scrollIndicator.style.left = `${maximum > 0 ? (position / maximum) * indicatorTravel : 0}px`;

        const activeSlide = position >= maximum - 1
          ? starts.length
          : starts.filter((start) => start <= position + 2).length;
        if (currentSlide) currentSlide.textContent = Math.max(1, activeSlide);
        if (totalSlides) totalSlides.textContent = starts.length;
      };

      let measureFrame = null;
      const measureStory = () => {
        if (measureFrame) cancelAnimationFrame(measureFrame);
        measureFrame = requestAnimationFrame(() => {
          measureFrame = null;
          gallery.style.removeProperty('--mp-rendered-card-height');
          const minimum = parseFloat(getComputedStyle(gallery).getPropertyValue('--mp-card-height')) || 0;
          const finalStyles = finalCard ? getComputedStyle(finalCard) : null;
          const finalHeight = finalCardContent && finalStyles
            ? finalCardContent.scrollHeight + parseFloat(finalStyles.paddingTop) + parseFloat(finalStyles.paddingBottom)
            : 0;
          gallery.style.setProperty('--mp-rendered-card-height', `${Math.ceil(Math.max(minimum, story.scrollHeight, finalHeight))}px`);
          sizeColumns();
          updateControls();
        });
      };

      const scrollToNextSlide = (direction) => {
        const position = viewport.scrollLeft;
        const starts = slideStarts();
        const target = direction > 0
          ? starts.find((start) => start > position + 2) ?? maxScroll()
          : starts.reverse().find((start) => start < position - 2) ?? 0;
        viewport.scrollTo({ left: Math.max(0, Math.min(maxScroll(), target)), behavior: 'smooth' });
      };

      previousButton.addEventListener('click', () => scrollToNextSlide(-1));
      nextButton.addEventListener('click', () => scrollToNextSlide(1));
      viewport.addEventListener('scroll', updateControls, { passive: true });

      let galleryDrag = null;
      let suppressClick = false;
      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        galleryDrag = { id: event.pointerId, x: event.clientX, scrollLeft: viewport.scrollLeft, moved: false };
      });
      viewport.addEventListener('pointermove', (event) => {
        if (!galleryDrag || event.pointerId !== galleryDrag.id) return;
        const distance = event.clientX - galleryDrag.x;
        if (!galleryDrag.moved && Math.abs(distance) < 6) return;
        if (!galleryDrag.moved) {
          galleryDrag.moved = true;
          viewport.classList.add('is-dragging');
          viewport.setPointerCapture(event.pointerId);
        }
        viewport.scrollLeft = galleryDrag.scrollLeft - distance;
        event.preventDefault();
      });
      const finishGalleryDrag = (event) => {
        if (!galleryDrag || event.pointerId !== galleryDrag.id) return;
        suppressClick = galleryDrag.moved;
        if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        viewport.classList.remove('is-dragging');
        galleryDrag = null;
        setTimeout(() => { suppressClick = false; }, 0);
      };
      viewport.addEventListener('pointerup', finishGalleryDrag);
      viewport.addEventListener('pointercancel', finishGalleryDrag);
      viewport.addEventListener('dragstart', (event) => event.preventDefault());
      viewport.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClick = false;
      }, true);

      let indicatorDrag = null;
      scrollIndicator.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        indicatorDrag = { id: event.pointerId, x: event.clientX, scrollLeft: viewport.scrollLeft };
        viewport.classList.add('is-dragging');
        scrollIndicator.setPointerCapture(event.pointerId);
      });
      scrollIndicator.addEventListener('pointermove', (event) => {
        if (!indicatorDrag || event.pointerId !== indicatorDrag.id) return;
        const travel = scrollTrack.clientWidth - scrollIndicator.clientWidth;
        if (travel > 0) viewport.scrollLeft = indicatorDrag.scrollLeft + (event.clientX - indicatorDrag.x) * maxScroll() / travel;
      });
      const finishIndicatorDrag = (event) => {
        if (!indicatorDrag || event.pointerId !== indicatorDrag.id) return;
        if (scrollIndicator.hasPointerCapture(event.pointerId)) scrollIndicator.releasePointerCapture(event.pointerId);
        viewport.classList.remove('is-dragging');
        indicatorDrag = null;
      };
      scrollIndicator.addEventListener('pointerup', finishIndicatorDrag);
      scrollIndicator.addEventListener('pointercancel', finishIndicatorDrag);

      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(measureStory);
        observer.observe(viewport);
        if (storyContent) observer.observe(storyContent);
        if (finalCardContent) observer.observe(finalCardContent);
      } else {
        window.addEventListener('resize', measureStory);
      }
      if (document.fonts?.ready) document.fonts.ready.then(measureStory);
      measureStory();

      const lightbox = gallery.querySelector('[data-gallery-lightbox]');
      const lightboxTriggers = Array.from(gallery.querySelectorAll('[data-lightbox-trigger]'));
      if (!lightbox || !lightboxTriggers.length || typeof lightbox.showModal !== 'function') return;

      const lightboxImage = lightbox.querySelector('[data-lightbox-image]');
      const lightboxCount = lightbox.querySelector('[data-lightbox-count]');
      const lightboxClose = lightbox.querySelector('[data-lightbox-close]');
      const lightboxPrevious = lightbox.querySelector('[data-lightbox-previous]');
      const lightboxNext = lightbox.querySelector('[data-lightbox-next]');
      let activeImage = 0;
      let openedBy = null;
      let previousOverflow = '';

      const showLightboxImage = (index) => {
        activeImage = (index + lightboxTriggers.length) % lightboxTriggers.length;
        const trigger = lightboxTriggers[activeImage];
        lightboxImage.src = trigger.href;
        lightboxImage.alt = trigger.querySelector('img')?.alt || '';
        lightboxCount.textContent = `${activeImage + 1} / ${lightboxTriggers.length}`;
      };

      const hasMultipleImages = lightboxTriggers.length > 1;
      lightboxPrevious.hidden = !hasMultipleImages;
      lightboxNext.hidden = !hasMultipleImages;

      lightboxTriggers.forEach((trigger, index) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          openedBy = trigger;
          showLightboxImage(index);
          previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          lightbox.showModal();
          lightboxClose.focus();
        });
      });

      lightboxClose.addEventListener('click', () => lightbox.close());
      lightboxPrevious.addEventListener('click', () => showLightboxImage(activeImage - 1));
      lightboxNext.addEventListener('click', () => showLightboxImage(activeImage + 1));

      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          showLightboxImage(activeImage - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          showLightboxImage(activeImage + 1);
        }
      });

      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) lightbox.close();
      });

      lightbox.addEventListener('close', () => {
        document.body.style.overflow = previousOverflow;
        lightboxImage.removeAttribute('src');
        openedBy?.focus();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initGallery(document));
  } else {
    initGallery(document);
  }
  document.addEventListener('shopify:section:load', (event) => initGallery(event.target));
})();
